import { BX } from '../internal/global';

/**
 * `BX.userOptions`: deferred saving of the user's interface settings.
 *
 * Transcribed from `core_ajax.js`. `save()` collects the values, mirrors them into the
 * `<COOKIE_PREFIX>_LAST_SETTINGS` cookie and sends them in one `main.userOption.saveOptions` call
 * after `delay` milliseconds. The methods reach the state and each other through
 * `BX.userOptions`, not through this object, so a caller who swaps a member - or the whole object -
 * is still the one that gets called.
 */

export interface LegacyUserOptions
{
	options: Record<string, any> | null;
	bSend: boolean;
	delay: number;
	path: string;
	setAjaxPath(url: any): void;
	save(category: any, name: any, valueName: any, value: any, common?: any): void;
	send(callback: any): void;
	del(category: any, name: any, common?: any, callback?: any): void;
	__get_values(options: { backwardCompatibility?: boolean }): any;
	__get(): string;
}

export const userOptions: LegacyUserOptions = {
	options: null,
	bSend: false,
	delay: 5000,
	path: '/bitrix/admin/user_options.php?',

	setAjaxPath(url: any): void
	{
		console.warn('BX.userOptions.setAjaxPath is deprecated. There is no way to change ajax path.');
	},

	save(category: any, name: any, valueName: any, value: any, common?: any): void
	{
		if (BX.userOptions.options === null)
		{
			BX.userOptions.options = {};
		}

		common = Boolean(common);
		BX.userOptions.options[`${category}.${name}.${valueName}`] = [category, name, valueName, value, common];

		const stringPackedValue = BX.userOptions.__get();
		if (stringPackedValue)
		{
			document.cookie = `${BX.message('COOKIE_PREFIX')}_LAST_SETTINGS=${encodeURIComponent(stringPackedValue)}&sessid=${BX.bitrix_sessid()}; expires=Thu, 31 Dec ${(new Date()).getFullYear() + 1} 23:59:59 GMT; path=/;`;
		}

		if (!BX.userOptions.bSend)
		{
			BX.userOptions.bSend = true;
			setTimeout(() => {
				BX.userOptions.send(null);
			}, BX.userOptions.delay);
		}
	},

	send(callback: any): void
	{
		const values = BX.userOptions.__get_values({ backwardCompatibility: true });

		BX.userOptions.options = null;
		BX.userOptions.bSend = false;

		if (values)
		{
			document.cookie = `${BX.message('COOKIE_PREFIX')}_LAST_SETTINGS=; path=/;`;

			// eslint-disable-next-line promise/catch-or-return -- saving is fire-and-forget, the callback is optional
			BX.ajax.runAction(
				'main.userOption.saveOptions',
				{
					json: {
						newValues: values,
					},
				},
			).then((response: any) => {
				// oxlint-disable-next-line promise/no-callback-in-promise -- the callback is the public signature
				if (BX.type.isFunction(callback))
				{
					// eslint-disable-next-line promise/no-callback-in-promise -- the callback is the public signature
					callback(response);
				}
			});
		}
	},

	del(category: any, name: any, common?: any, callback?: any): void
	{
		// eslint-disable-next-line promise/catch-or-return -- see send()
		BX.ajax.runAction(
			'main.userOption.deleteOption',
			{
				json: {
					category,
					name,
					common,
				},
			},
		).then((response: any) => {
			// oxlint-disable-next-line promise/no-callback-in-promise -- the callback is the public signature
			if (BX.type.isFunction(callback))
			{
				// eslint-disable-next-line promise/no-callback-in-promise -- the callback is the public signature
				callback(response);
			}
		});
	},

	__get_values({ backwardCompatibility }: { backwardCompatibility?: boolean }): any
	{
		if (!BX.userOptions || !BX.Type.isPlainObject(BX.userOptions.options))
		{
			return null;
		}

		const CATEGORY = 0;
		const NAME = 1;
		const VALUE_NAME = 2;
		const VALUE = 3;
		const IS_DEFAULT = 4;

		const packedValues: { p: any[] } = { p: [] };
		let currentIndex = -1;
		let previousOptionIdentifier = '';

		Object.entries(BX.userOptions.options).forEach(([key, userOption]: [string, any]) => {
			const category = userOption[CATEGORY];
			const name = userOption[NAME];
			const currentOptionIdentifier = `${category}.${name}`;

			if (previousOptionIdentifier !== currentOptionIdentifier)
			{
				currentIndex++;
				packedValues.p.push({
					c: category,
					n: name,
					v: {},
				});
				if (userOption[IS_DEFAULT] === true)
				{
					packedValues.p[currentIndex].d = 'Y';
				}
				previousOptionIdentifier = currentOptionIdentifier;
			}

			if (userOption[VALUE_NAME] === null)
			{
				packedValues.p[currentIndex].v = userOption[VALUE];
			}
			else
			{
				let data = userOption[VALUE];
				if (backwardCompatibility && Array.isArray(userOption[VALUE]))
				{
					data = userOption[VALUE].join(',');
				}
				packedValues.p[currentIndex].v[userOption[VALUE_NAME]] = data;
			}
		});

		return packedValues.p.length > 0 ? packedValues.p : null;
	},

	/**
	 * @deprecated Use instead BX.userOptions.__get_values.
	 */
	__get(): string
	{
		if (!BX.userOptions.options)
		{
			return '';
		}

		let sParam = '';
		let n = -1;
		let prevParam = '';
		let aOpt;

		for (const i in BX.userOptions.options)
		{
			if (BX.userOptions.options.hasOwnProperty(i))
			{
				aOpt = BX.userOptions.options[i];

				if (prevParam != aOpt[0] + '.' + aOpt[1])
				{
					n++;
					sParam += '&p[' + n + '][c]=' + BX.util.urlencode(aOpt[0]);
					sParam += '&p[' + n + '][n]=' + BX.util.urlencode(aOpt[1]);
					if (aOpt[4] == true)
					{
						sParam += '&p[' + n + '][d]=Y';
					}
					prevParam = aOpt[0] + '.' + aOpt[1];
				}

				const valueName = aOpt[2];
				const value = aOpt[3];

				if (valueName === null)
				{
					sParam += '&p[' + n + '][v]=' + BX.util.urlencode(value);
				}
				else
				{
					sParam += '&p[' + n + '][v][' + BX.util.urlencode(valueName) + ']=' + BX.util.urlencode(value);
				}
			}
		}

		return sParam.substr(1);
	},
};
